/**
 * Work Orders — Phase 9 implementation.
 *
 * Page composition:
 *   - PageHeader + "Plan production run" action (opens WorkOrderForm in Sheet)
 *   - KPI strip (Planned this week / In progress / Completed MTD / Overdue)
 *   - Tab strip (status filter — All + every WO status)
 *   - DataTable
 *   - Detail Sheet on row click — header card, BOM snapshot, consumption,
 *     production batches, action bar (Start / Pause / Resume / Record /
 *     Complete / Cancel)
 *
 * Mock-aware: when `useMockData()` is true, reads come from `productionMock`.
 * Real wiring would use InstantDB queries.
 */

import * as React from "react";
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Hammer, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatRelative } from "@bizop/shared/dates";
import { formatMoney } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useCurrentRole } from "../../../lib/permissions";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { WorkOrderStatusBadge } from "../../../components/production/WorkOrderStatusBadge";
import { PriorityBadge } from "../../../components/production/PriorityBadge";
import { ConsumptionTable } from "../../../components/production/ConsumptionTable";
import {
  ProductionTimeline,
  type WorkOrderTimelineEntry,
} from "../../../components/production/ProductionTimeline";
import {
  WorkOrderActionsBar,
  type WorkOrderAction,
} from "../../../components/production/WorkOrderActionsBar";
import { WorkOrderRowActions } from "../../../components/production/WorkOrderRowActions";
import { WorkOrderProgress } from "../../../components/production/WorkOrderProgress";
import { WorkOrderTimer } from "../../../components/production/WorkOrderTimer";
import { ProductionEconomics } from "../../../components/production/ProductionEconomics";
import {
  WorkOrderForm,
  type WorkOrderFormValues,
} from "../../../components/forms/WorkOrderForm";
import { RecordProductionForm } from "../../../components/forms/RecordProductionForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  consumptionForWO,
  kpis as kpisFromMock,
  mockWorkOrders,
  productionForWO,
  type MockWorkOrder,
  type WorkOrderStatus,
} from "../../../lib/productionMock";
import {
  mockBoms,
  mockBomLines,
  mockProducts,
  mockUnits,
  type MockBom,
  type MockBomLine,
  type MockProduct,
  type MockUnit,
} from "../../../lib/catalogMock";
import {
  mockStockMovements,
  mockWarehouses,
  type MockStockMovement,
  type MockWarehouse,
} from "../../../lib/inventoryMock";

export const Route = createFileRoute("/_app/production/work-orders")({
  component: WorkOrdersPage,
});

type TabValue = "all" | WorkOrderStatus;

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: "all", label: "All" },
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In progress" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

function formatDateShort(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function WorkOrdersPage() {
  const isMock = useMockData();
  const role = useCurrentRole();
  // Hide landed cost / consumption value for any role that doesn't have
  // accounts.read (the perm that gates GL-style cost visibility). Office
  // managers, sales, and warehouse all fall on the wrong side of this line.
  const hideCost =
    role === "warehouse" ||
    role === "sales" ||
    role === "office_manager";

  const [rows, setRows] = usePersistentMockState<MockWorkOrder[]>("workOrders", mockWorkOrders);
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [boms] = usePersistentMockState<MockBom[]>("boms", mockBoms);
  const [bomLines] = usePersistentMockState<MockBomLine[]>(
    "bomLines",
    mockBomLines,
  );
  const [units] = usePersistentMockState<MockUnit[]>("units", mockUnits);
  const [warehouses] = usePersistentMockState<MockWarehouse[]>(
    "warehouses",
    mockWarehouses,
  );
  const [stockMovements] = usePersistentMockState<MockStockMovement[]>(
    "stockMovements",
    mockStockMovements,
  );

  const [tab, setTab] = useState<TabValue>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockWorkOrder | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);

  const kpis = useMemo(() => kpisFromMock(), []);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab]);

  const productsById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );
  const bomsById = useMemo(
    () => new Map(boms.map((b) => [b._id, b])),
    [boms],
  );
  const warehousesById = useMemo(
    () => new Map(warehouses.map((w) => [w._id, w])),
    [warehouses],
  );

  /* Map of WO id → has any production batch (gates the Complete chip). */
  const productionCountByWoId = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      map.set(r._id, productionForWO(r._id).length);
    }
    return map;
  }, [rows]);

  /* ── Columns ── */
  const columns = useMemo<ColumnDef<MockWorkOrder>[]>(
    () => [
      {
        accessorKey: "workOrderNumber",
        header: "WO #",
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-[12px] text-(--text-primary)">
              {row.original.workOrderNumber}
            </span>
            {/* Live timer for in-progress runs — small + monospace. */}
            <WorkOrderTimer workOrder={row.original} />
          </div>
        ),
        meta: { mono: true },
      },
      {
        id: "product",
        header: "Output product",
        cell: ({ row }) => (
          <span className="text-(--text-primary)">
            {productsById.get(row.original.outputProductId)?.displayName ??
              "—"}
          </span>
        ),
      },
      {
        id: "bomVersion",
        header: "BOM",
        cell: ({ row }) => (
          <span className="text-[11px] font-mono text-(--text-tertiary)">
            {bomsById.get(row.original.bomId)?.versionLabel ?? "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "plannedQuantity",
        header: "Planned",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.plannedQuantity}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        accessorKey: "actualQuantity",
        header: "Actual",
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.actualQuantity}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        id: "factory",
        header: "Factory",
        cell: ({ row }) => (
          <Badge variant="neutral">
            {warehousesById.get(row.original.factoryWarehouseId)?.code ?? "—"}
          </Badge>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <WorkOrderStatusBadge status={row.original.status} />
        ),
      },
      {
        id: "progress",
        header: "Progress",
        cell: ({ row }) => (
          <div className="min-w-[140px] max-w-[200px]">
            <WorkOrderProgress workOrder={row.original} compact />
          </div>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "priority",
        header: "Priority",
        cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: ({ row }) => (
          <span
            className="text-(--text-secondary) tabular-nums"
            title={formatDateShort(row.original.updatedAt)}
          >
            {formatRelative(row.original.updatedAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "rowActions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <WorkOrderRowActions
            status={row.original.status}
            workOrderNumber={row.original.workOrderNumber}
            plannedQuantity={row.original.plannedQuantity}
            factoryWarehouseName={
              warehousesById.get(row.original.factoryWarehouseId)?.displayName ??
              "the factory warehouse"
            }
            hasProduction={(productionCountByWoId.get(row.original._id) ?? 0) > 0}
            onAction={(action) => handleAction(row.original, action)}
          />
        ),
        meta: { numeric: true },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productsById, bomsById, warehousesById, productionCountByWoId],
  );

  /* ── Mutation handlers (mock-only writes for now). ── */
  function handleCreate(values: WorkOrderFormValues) {
    if (!isMock) {
      toast.info("Wired through InstantDB in real mode");
      return;
    }
    const now = Date.now();
    const product = products.find((p) => p._id === values.outputProductId);
    const activeBom = boms.find(
      (b) => b.productId === values.outputProductId && b.isActive,
    );
    if (!product || !activeBom) {
      toast.error("No active BOM for that product");
      return;
    }
    const nextNum = `WO-2026-${String(rows.length + 1).padStart(6, "0")}`;
    const newRow: MockWorkOrder = {
      _id: `wo_local_${now}`,
      workOrderNumber: nextNum,
      outputProductId: values.outputProductId,
      outputVariantId: null,
      bomId: activeBom._id,
      plannedQuantity: values.plannedQuantity,
      actualQuantity: 0,
      outputUnitId: activeBom.outputUnitId,
      factoryWarehouseId: values.factoryWarehouseId,
      outputWarehouseId: values.outputWarehouseId,
      plannedStartAt: values.plannedStartAt,
      plannedEndAt: values.plannedEndAt,
      actualStartedAt: null,
      actualCompletedAt: null,
      status: "planned",
      priority: values.priority,
      assignedSupervisorUserId: "dev-user",
      notes: values.notes || null,
      createdAt: now,
      createdByUserId: "dev-user",
      updatedAt: now,
      updatedByUserId: "dev-user",
    };
    setRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`Planned ${nextNum} (mock)`);
  }

  function handleAction(
    wo: MockWorkOrder,
    action: WorkOrderAction,
    payload?: { reason?: string },
  ) {
    if (!isMock) {
      toast.info(`Wired through InstantDB in real mode (${action})`);
      return;
    }
    if (action === "record_production") {
      setRecordOpen(true);
      return;
    }
    const now = Date.now();
    const transitions: Record<
      Exclude<WorkOrderAction, "record_production">,
      WorkOrderStatus
    > = {
      start: "in_progress",
      pause: "paused",
      resume: "in_progress",
      complete: "completed",
      cancel: "cancelled",
    };
    const next = transitions[action as Exclude<WorkOrderAction, "record_production">];
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== wo._id) return r;
        const updated: Partial<MockWorkOrder> = {
          status: next,
          updatedAt: now,
        };
        if (action === "start") updated.actualStartedAt = now;
        if (action === "complete") updated.actualCompletedAt = now;
        if (action === "cancel") {
          updated.notes = payload?.reason
            ? `${r.notes ?? ""}\n[Cancelled] ${payload.reason}`.trim()
            : r.notes;
        }
        return { ...r, ...updated };
      }),
    );
    if (detailRow && detailRow._id === wo._id) {
      setDetailRow((prev) =>
        prev
          ? { ...prev, status: next, updatedAt: now }
          : prev,
      );
    }
    toast.success(`${wo.workOrderNumber} → ${next}`);
  }

  function handleRecordProduction(values: {
    quantity: number;
    qualityGrade: "a" | "b" | "c" | "reject";
    notes: string;
  }) {
    if (!isMock || !detailRow) return;
    const now = Date.now();
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== detailRow._id) return r;
        const newActual =
          values.qualityGrade !== "reject"
            ? r.actualQuantity + values.quantity
            : r.actualQuantity;
        return { ...r, actualQuantity: newActual, updatedAt: now };
      }),
    );
    setDetailRow((prev) =>
      prev
        ? {
            ...prev,
            actualQuantity:
              values.qualityGrade !== "reject"
                ? prev.actualQuantity + values.quantity
                : prev.actualQuantity,
            updatedAt: now,
          }
        : prev,
    );
    setRecordOpen(false);
    toast.success(
      `Logged ${values.quantity} unit${values.quantity === 1 ? "" : "s"} (grade ${values.qualityGrade.toUpperCase()})`,
    );
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockWorkOrder>[] = [
      { key: "workOrderNumber", label: "WO #" },
      {
        key: "outputProductId",
        label: "Output product",
        value: (r) => productsById.get(r.outputProductId)?.displayName ?? r.outputProductId,
      },
      {
        key: "bomId",
        label: "BOM",
        value: (r) => bomsById.get(r.bomId)?.versionLabel ?? r.bomId,
      },
      { key: "plannedQuantity", label: "Planned qty" },
      { key: "actualQuantity", label: "Actual qty" },
      {
        key: "factoryWarehouseId",
        label: "Factory",
        value: (r) =>
          warehousesById.get(r.factoryWarehouseId)?.code ?? r.factoryWarehouseId,
      },
      {
        key: "outputWarehouseId",
        label: "Output WH",
        value: (r) =>
          warehousesById.get(r.outputWarehouseId)?.code ?? r.outputWarehouseId,
      },
      { key: "status", label: "Status" },
      { key: "priority", label: "Priority" },
      { key: "plannedStartAt", label: "Planned start", format: (v) => formatDateForCsv(Number(v)) },
      { key: "plannedEndAt", label: "Planned end", format: (v) => formatDateForCsv(Number(v)) },
      {
        key: "actualStartedAt",
        label: "Actual start",
        format: (v) => formatDateForCsv(v as number | null),
      },
      {
        key: "actualCompletedAt",
        label: "Actual end",
        format: (v) => formatDateForCsv(v as number | null),
      },
      { key: "updatedAt", label: "Updated", format: (v) => formatDateForCsv(Number(v)) },
    ];
    downloadCsv(
      exportFilename("work-orders", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Work Orders"
        description="Plan, run, and track manufacturing runs from raw material issue to finished-goods receipt."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Plan production run
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Work order summary"
      >
        <KpiCard
          caption="Planned this week"
          value={String(kpis.plannedThisWeek)}
          hint="New runs starting in the next 7 days"
        />
        <KpiCard
          caption="In progress"
          value={String(kpis.inProgress)}
          hint="Active and paused runs"
        />
        <KpiCard
          caption="Completed MTD"
          value={String(kpis.completedThisMonth)}
          hint="Runs finished this month"
        />
        <KpiCard
          caption="Overdue"
          value={String(kpis.overdueCount)}
          hint="Past planned end without completion"
          delta={
            kpis.overdueCount > 0
              ? { value: `${kpis.overdueCount}`, trend: "down", label: "needs attention" }
              : undefined
          }
        />
      </section>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
        <TabsList>
          {TAB_OPTIONS.map((opt) => (
            <TabsTrigger key={opt.value} value={opt.value}>
              {opt.label}
              <span className="ml-2 text-(--text-tertiary)">
                {opt.value === "all"
                  ? rows.length
                  : rows.filter((r) => r.status === opt.value).length}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <DataTable
            data={filtered}
            columns={columns}
            getRowId={(r) => r._id}
            onRowClick={(r) => setDetailRow(r.original)}
            emptyIcon={<Hammer className="h-6 w-6" />}
            emptyTitle="No work orders"
            emptyDescription={
              tab === "all"
                ? "Plan a production run to begin tracking output."
                : `No work orders are ${TAB_OPTIONS.find((o) => o.value === tab)?.label.toLowerCase()}.`
            }
          />
        </TabsContent>
      </Tabs>

      {/* Create */}
      <Sheet
        open={createOpen}
        onOpenChange={(open) => !open && setCreateOpen(false)}
      >
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>Plan production run</SheetTitle>
            <SheetDescription>
              Pick the output product, quantity and dates. The active BOM is
              snapshotted onto this work order so component math is stable.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <WorkOrderForm
              products={products
                .filter((p) => p.productKind === "manufactured")
                .map((p) => ({
                  id: p._id,
                  displayName: p.displayName,
                  sku: p.sku,
                  hasActiveBom: boms.some(
                    (b) => b.productId === p._id && b.isActive,
                  ),
                }))}
              factoryWarehouses={warehouses
                .filter((w) => w.warehouseType === "raw_material")
                .map((w) => ({ id: w._id, displayName: w.displayName }))}
              outputWarehouses={warehouses
                .filter((w) => w.warehouseType === "finished_goods")
                .map((w) => ({ id: w._id, displayName: w.displayName }))}
              resolveRequirements={(productId) => {
                const bom = boms.find(
                  (b) => b.productId === productId && b.isActive,
                );
                if (!bom) return [];
                return bomLines
                  .filter((l) => l.bomId === bom._id)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((l) => ({
                    componentName:
                      productsById.get(l.componentProductId)?.displayName ??
                      "—",
                    perUnitQty: l.quantity,
                    unitCode:
                      units.find((u) => u._id === l.unitId)?.code ?? "pcs",
                  }));
              }}
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setRecordOpen(false);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow ? (
            <WorkOrderDetail
              row={detailRow}
              hideCost={hideCost}
              onAction={(action, payload) =>
                handleAction(detailRow, action, payload)
              }
              recordOpen={recordOpen}
              onRecordOpenChange={setRecordOpen}
              onRecordSubmit={handleRecordProduction}
              products={products}
              boms={boms}
              units={units}
              warehouses={warehouses}
              stockMovements={stockMovements}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

function WorkOrderDetail({
  row,
  hideCost,
  onAction,
  recordOpen,
  onRecordOpenChange,
  onRecordSubmit,
  products,
  boms,
  units,
  warehouses,
  stockMovements,
}: {
  row: MockWorkOrder;
  hideCost: boolean;
  onAction: (
    action: WorkOrderAction,
    payload?: { reason?: string },
  ) => void;
  recordOpen: boolean;
  onRecordOpenChange: (open: boolean) => void;
  onRecordSubmit: (values: {
    quantity: number;
    qualityGrade: "a" | "b" | "c" | "reject";
    notes: string;
  }) => void;
  products: MockProduct[];
  boms: MockBom[];
  units: MockUnit[];
  warehouses: MockWarehouse[];
  stockMovements: MockStockMovement[];
}) {
  const product = useMemo(
    () => products.find((p) => p._id === row.outputProductId),
    [products, row.outputProductId],
  );
  const bom = useMemo(
    () => boms.find((b) => b._id === row.bomId),
    [boms, row.bomId],
  );
  const factoryWh = useMemo(
    () => warehouses.find((w) => w._id === row.factoryWarehouseId),
    [warehouses, row.factoryWarehouseId],
  );
  const outputWh = useMemo(
    () => warehouses.find((w) => w._id === row.outputWarehouseId),
    [warehouses, row.outputWarehouseId],
  );
  const consumption = consumptionForWO(row._id);
  const production = productionForWO(row._id);

  // Build timeline.
  const timeline = useMemo<WorkOrderTimelineEntry[]>(() => {
    const out: WorkOrderTimelineEntry[] = [];
    out.push({
      kind: "transition",
      fromStatus: null,
      toStatus: "planned",
      changedAt: row.createdAt,
      changedByUserId: row.createdByUserId,
    });
    if (row.actualStartedAt !== null) {
      out.push({
        kind: "transition",
        fromStatus: "planned",
        toStatus: "in_progress",
        changedAt: row.actualStartedAt,
        changedByUserId: row.updatedByUserId,
      });
    }
    if (row.status === "paused") {
      out.push({
        kind: "transition",
        fromStatus: "in_progress",
        toStatus: "paused",
        changedAt: row.updatedAt,
        changedByUserId: row.updatedByUserId,
        reason: row.notes ?? null,
      });
    }
    for (const p of production) {
      out.push({
        kind: "production",
        producedAt: p.producedAt,
        quantity: p.quantity,
        qualityGrade: p.qualityGrade,
        landedCostInBaseCents: p.landedCostInBaseCents,
        changedByUserId: row.updatedByUserId,
      });
    }
    if (row.actualCompletedAt !== null) {
      out.push({
        kind: "transition",
        fromStatus: "in_progress",
        toStatus: "completed",
        changedAt: row.actualCompletedAt,
        changedByUserId: row.updatedByUserId,
      });
    }
    if (row.status === "cancelled") {
      out.push({
        kind: "transition",
        fromStatus: null,
        toStatus: "cancelled",
        changedAt: row.updatedAt,
        changedByUserId: row.updatedByUserId,
        reason: row.notes ?? null,
      });
    }
    return out.sort((a, b) => {
      const ta = a.kind === "transition" ? a.changedAt : a.producedAt;
      const tb = b.kind === "transition" ? b.changedAt : b.producedAt;
      return ta - tb;
    });
  }, [row, production]);

  // Build consumption table rows with mock product names + on-hand
  // derived from the mock stock-movement totals at the source warehouse.
  const consumptionRows = useMemo(
    () =>
      consumption.map((c) => {
        const componentProduct = products.find(
          (p) => p._id === c.componentProductId,
        );
        const unit = units.find((u) => u._id === c.unitId);
        let onHand = 0;
        for (const m of stockMovements) {
          if (
            m.productId === c.componentProductId &&
            m.warehouseId === c.consumedFromWarehouseId
          ) {
            onHand += m.quantityDelta;
          }
        }
        return {
          consumption: c,
          productName: componentProduct?.displayName ?? "—",
          unitCode: unit?.code ?? "pcs",
          onHand,
        };
      }),
    [consumption, products, units, stockMovements],
  );

  const totalConsumedValue = useMemo(() => {
    let v = 0n;
    for (const c of consumption) {
      v +=
        c.costPerUnitInBaseCents *
        BigInt(Math.trunc(c.actualQuantity));
    }
    return v;
  }, [consumption]);

  const remainingQty = Math.max(0, row.plannedQuantity - row.actualQuantity);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{row.workOrderNumber}</span>
          <WorkOrderStatusBadge status={row.status} />
          <PriorityBadge priority={row.priority} />
          <WorkOrderTimer workOrder={row} className="ml-auto" />
        </SheetTitle>
        <SheetDescription>
          {product?.displayName ?? "—"} · planned{" "}
          {formatDateShort(row.plannedStartAt)} →{" "}
          {formatDateShort(row.plannedEndAt)}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        {/* Phase 9: at-a-glance progress bar — replaces the previous static
            status timeline at the top of the Sheet. The full state-machine
            timeline still lives under the Timeline tab. */}
        <div className="mb-5">
          <WorkOrderProgress workOrder={row} />
        </div>

        {/* Phase 9: cost / earnings / output / loss strip. Finance-gated. */}
        <ProductionEconomics
          workOrder={row}
          consumption={consumption}
          production={production}
          products={products}
          className="mb-5"
        />

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="consumption">
              Consumption
              <span className="ml-2 text-(--text-tertiary)">
                {consumption.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="production">
              Production
              <span className="ml-2 text-(--text-tertiary)">
                {production.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
              <section className="grid grid-cols-2 gap-3">
                <DetailField
                  label="Planned qty"
                  value={
                    <span className="font-mono tabular-nums">
                      {row.plannedQuantity}
                    </span>
                  }
                />
                <DetailField
                  label="Actual qty"
                  value={
                    <span className="font-mono tabular-nums">
                      {row.actualQuantity}{" "}
                      <span className="text-(--text-tertiary)">
                        / {row.plannedQuantity}
                      </span>
                    </span>
                  }
                />
                <DetailField
                  label="Factory warehouse"
                  value={factoryWh?.displayName ?? "—"}
                />
                <DetailField
                  label="Output warehouse"
                  value={outputWh?.displayName ?? "—"}
                />
                <DetailField
                  label="Started"
                  value={
                    row.actualStartedAt !== null
                      ? formatDateShort(row.actualStartedAt)
                      : "—"
                  }
                />
                <DetailField
                  label="Completed"
                  value={
                    row.actualCompletedAt !== null
                      ? formatDateShort(row.actualCompletedAt)
                      : "—"
                  }
                />
                {!hideCost ? (
                  <DetailField
                    label="Component value (issued)"
                    value={
                      <span className="font-mono tabular-nums">
                        {formatMoney(totalConsumedValue, "BDT", {
                          locale: "en-BD",
                        })}
                      </span>
                    }
                  />
                ) : null}
                <DetailField
                  label="Yield"
                  value={
                    row.plannedQuantity > 0 ? (
                      <span className="font-mono tabular-nums">
                        {Math.round(
                          (row.actualQuantity / row.plannedQuantity) * 100,
                        )}
                        %
                      </span>
                    ) : (
                      "—"
                    )
                  }
                />
              </section>

              {/* BOM snapshot card */}
              {bom ? (
                <section className="rounded-[var(--radius-md)] border border-(--border-subtle) p-3 flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-(--text-primary)">
                      BOM snapshot — {bom.code}
                    </span>
                    <Badge variant="info">{bom.versionLabel}</Badge>
                  </div>
                  <div className="text-[12px] text-(--text-secondary)">
                    {bom.notes ?? "—"}
                  </div>
                </section>
              ) : null}

              {row.notes ? (
                <section className="rounded-[var(--radius-md)] bg-(--bg-canvas) p-3 text-[12px] text-(--text-secondary) whitespace-pre-line">
                  {row.notes}
                </section>
              ) : null}
            </div>
          </TabsContent>

          {/* Consumption */}
          <TabsContent value="consumption" className="mt-4">
            <ConsumptionTable rows={consumptionRows} hideCost={hideCost} />
          </TabsContent>

          {/* Production */}
          <TabsContent value="production" className="mt-4">
            {production.length === 0 ? (
              <div className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) p-4 text-center text-[12px] text-(--text-tertiary)">
                No production batches yet.
              </div>
            ) : (
              <div className="overflow-hidden rounded-[var(--radius-md)] border border-(--border-subtle)">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-(--bg-canvas) text-left text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                      <th className="px-2 py-2">Batch</th>
                      <th className="px-2 py-2 w-24 text-right">Qty</th>
                      <th className="px-2 py-2 w-20">Grade</th>
                      {!hideCost ? (
                        <th className="px-2 py-2 w-32 text-right">
                          Landed cost
                        </th>
                      ) : null}
                      <th className="px-2 py-2 w-32 text-right">Produced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {production.map((p) => (
                      <tr
                        key={p._id}
                        className="border-t border-(--border-subtle)"
                      >
                        <td className="px-2 py-1.5 font-mono text-[11px] text-(--text-tertiary)">
                          {p._id}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                          {p.quantity}
                        </td>
                        <td className="px-2 py-1.5 uppercase">
                          {p.qualityGrade}
                        </td>
                        {!hideCost ? (
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                            {formatMoney(p.landedCostInBaseCents, "BDT", {
                              locale: "en-BD",
                            })}
                          </td>
                        ) : null}
                        <td className="px-2 py-1.5 text-right text-(--text-secondary) tabular-nums">
                          {formatRelative(p.producedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Timeline */}
          <TabsContent value="timeline" className="mt-4">
            <ProductionTimeline entries={timeline} />
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="mt-6 border-t border-(--border-subtle) pt-4">
          <WorkOrderActionsBar
            status={row.status}
            workOrderNumber={row.workOrderNumber}
            hasProduction={production.length > 0}
            onAction={onAction}
          />
        </div>

        {/* Record-production inline area */}
        {recordOpen ? (
          <div className="mt-4 rounded-[var(--radius-md)] border border-(--border-subtle) p-4 bg-(--bg-canvas)">
            <div className="text-[13px] font-medium text-(--text-primary) mb-3">
              Record production batch
            </div>
            <RecordProductionForm
              remainingQuantity={remainingQty}
              onSubmit={onRecordSubmit}
              onCancel={() => onRecordOpenChange(false)}
            />
          </div>
        ) : null}
      </SheetBody>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <span className="text-[13px] text-(--text-primary)">{value}</span>
    </div>
  );
}
